import {
	Button,
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Separator,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Textarea,
} from "@monbits/ui";
import { ChevronRight, Loader2, Settings as SettingsIcon } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { StatusBadge } from "src/components/status-badge";
import settingsQueries from "src/queries/settings-queries";
import type { SystemSetting } from "src/types";

const ManageSystemSettingsPage: React.FC = () => {
	const [selectedSetting, setSelectedSetting] =
		React.useState<SystemSetting | null>(null);
	const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
	const [isSaving, setIsSaving] = React.useState(false);
	const [editValue, setEditValue] = React.useState<string>("");

	const {
		data: settings = [],
		isLoading,
		refetch,
	} = settingsQueries.listSystemSettings();

	const updateMutation = settingsQueries.updateSystemSetting();

	const getCurrentValue = (setting: SystemSetting): unknown => {
		if (setting.value !== undefined) {
			return setting.value;
		}
		// Fallback to legacy fields
		if (setting.valueString !== undefined && setting.valueString !== null)
			return setting.valueString;
		if (setting.valueFloat !== undefined && setting.valueFloat !== null)
			return setting.valueFloat;
		if (setting.valueInt !== undefined && setting.valueInt !== null)
			return setting.valueInt;
		if (setting.valueBool !== undefined && setting.valueBool !== null)
			return setting.valueBool;
		if (setting.valueJson !== undefined && setting.valueJson !== null)
			return setting.valueJson;
		return null;
	};

	const handleRowClick = (setting: SystemSetting) => {
		setSelectedSetting(setting);
		const currentValue = getCurrentValue(setting);
		if (typeof currentValue === "object" && currentValue !== null) {
			setEditValue(JSON.stringify(currentValue, null, 2));
		} else {
			setEditValue(currentValue?.toString() ?? "");
		}
		setIsDrawerOpen(true);
	};

	const handleSave = async () => {
		if (!selectedSetting) return;

		try {
			setIsSaving(true);

			let parsedValue: unknown = editValue.trim();

			if (!parsedValue) {
				parsedValue = null;
			} else {
				const metadataType = selectedSetting.metadata?.type;

				if (metadataType === "number" || metadataType === "float") {
					parsedValue = Number.parseFloat(parsedValue as string);
				} else if (metadataType === "int" || metadataType === "integer") {
					parsedValue = Number.parseInt(parsedValue as string, 10);
				} else if (metadataType === "boolean" || metadataType === "bool") {
					parsedValue =
						parsedValue === "true" ||
						parsedValue === "1" ||
						parsedValue === "yes";
				} else if (
					metadataType === "object" ||
					metadataType === "array" ||
					metadataType === "json"
				) {
					try {
						parsedValue = JSON.parse(parsedValue as string);
					} catch (_error) {
						toast.error("Invalid JSON format");
						return;
					}
				} else if (metadataType === "string") {
				} else if (!metadataType) {
					const str = parsedValue as string;
					if (
						(str.startsWith("{") && str.endsWith("}")) ||
						(str.startsWith("[") && str.endsWith("]"))
					) {
						try {
							parsedValue = JSON.parse(str);
						} catch {
							// Keep as string if JSON parse fails
						}
					} else if (/^-?\d+(\.\d+)?$/.test(str)) {
						parsedValue = str.includes(".")
							? Number.parseFloat(str)
							: Number.parseInt(str, 10);
					} else if (str === "true" || str === "false") {
						parsedValue = str === "true";
					}
				}
			}

			await updateMutation.mutateAsync({
				key: selectedSetting.key,
				data: { value: parsedValue },
			});

			toast.success("System setting updated successfully");
			setIsDrawerOpen(false);
			refetch();
		} catch (error) {
			console.error("Failed to update system setting:", error);
			toast.error("Failed to update system setting");
		} finally {
			setIsSaving(false);
		}
	};

	const formatDate = (dateStr: string): string => {
		const date = new Date(dateStr);
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const formatValue = (setting: SystemSetting): string => {
		const currentValue = getCurrentValue(setting);
		if (currentValue === null || currentValue === undefined) {
			return "-";
		}
		if (typeof currentValue === "object") {
			return "JSON Object";
		}
		if (typeof currentValue === "boolean") {
			return currentValue ? "True" : "False";
		}
		return String(currentValue);
	};

	const getValueTypeBadge = (setting: SystemSetting) => {
		const metadataType = setting.metadata?.type;
		const type = metadataType || setting.valueType || "string";

		const typeMap: Record<
			string,
			| "success"
			| "warning"
			| "error"
			| "info"
			| "default"
			| "purple"
			| "cyan"
			| "pink"
		> = {
			string: "cyan",
			float: "purple",
			number: "purple",
			int: "purple",
			integer: "purple",
			bool: "success",
			boolean: "success",
			json: "warning",
			object: "warning",
			array: "pink",
		};

		return (
			<StatusBadge type={typeMap[type] || "default"}>
				{type.toUpperCase()}
			</StatusBadge>
		);
	};

	const renderValueInput = (
		setting: SystemSetting,
		value: string,
		setValue: (val: string) => void,
	) => {
		const metadataType = setting.metadata?.type;
		const options = setting.metadata?.options as string[] | undefined;
		const isSecret = setting.metadata?.secret === true;
		const min = setting.metadata?.min as number | undefined;
		const max = setting.metadata?.max as number | undefined;

		const getLabel = () => {
			if (metadataType) {
				return `${metadataType.charAt(0).toUpperCase()}${metadataType.slice(1)} Value`;
			}
			return "Value";
		};

		// Boolean: Switch toggle
		if (metadataType === "boolean" || metadataType === "bool") {
			return (
				<div className="space-y-2">
					<Label htmlFor="value-edit">{getLabel()}</Label>
					<div className="flex items-center justify-between rounded-lg border p-4">
						<div className="space-y-0.5">
							<Label htmlFor="value-edit" className="text-base">
								{value === "true" || value === "1" ? "Enabled" : "Disabled"}
							</Label>
							<p className="text-sm text-muted-foreground">
								Toggle to change the setting value
							</p>
						</div>
						<Switch
							id="value-edit"
							checked={value === "true" || value === "1"}
							onCheckedChange={(checked) =>
								setValue(checked ? "true" : "false")
							}
						/>
					</div>
				</div>
			);
		}

		// String with options: Select dropdown
		if (options && Array.isArray(options) && options.length > 0) {
			return (
				<div className="space-y-2">
					<Label htmlFor="value-edit">{getLabel()}</Label>
					<Select value={value} onValueChange={setValue}>
						<SelectTrigger className="w-full">
							<SelectValue placeholder="Select a value" />
						</SelectTrigger>
						<SelectContent>
							{options.map((opt) => (
								<SelectItem key={opt} value={opt}>
									{opt}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<p className="text-xs text-muted-foreground">
						Choose from available options
					</p>
				</div>
			);
		}

		// Number types: Number input
		if (
			metadataType === "number" ||
			metadataType === "float" ||
			metadataType === "int" ||
			metadataType === "integer"
		) {
			const step =
				metadataType === "float" || metadataType === "number" ? "0.01" : "1";
			return (
				<div className="space-y-2">
					<Label htmlFor="value-edit">{getLabel()}</Label>
					<Input
						id="value-edit"
						type="number"
						value={value}
						onChange={(e) => setValue(e.target.value)}
						placeholder="Enter number"
						step={step}
						min={min}
						max={max}
					/>
					{(min !== undefined || max !== undefined) && (
						<p className="text-xs text-muted-foreground">
							{min !== undefined && max !== undefined
								? `Range: ${min} - ${max}`
								: min !== undefined
									? `Minimum: ${min}`
									: `Maximum: ${max}`}
						</p>
					)}
				</div>
			);
		}

		// JSON/Object/Array: Textarea
		if (
			metadataType === "object" ||
			metadataType === "array" ||
			metadataType === "json" ||
			(value && (value.startsWith("{") || value.startsWith("[")))
		) {
			return (
				<div className="space-y-2">
					<Label htmlFor="value-edit">{getLabel()}</Label>
					<Textarea
						id="value-edit"
						value={value}
						onChange={(e) => setValue(e.target.value)}
						placeholder="Enter JSON value"
						className="min-h-[200px] font-mono text-sm"
					/>
					<p className="text-xs text-muted-foreground">
						Enter valid JSON format
					</p>
				</div>
			);
		}

		// Secret string: Password input
		if (isSecret) {
			return (
				<div className="space-y-2">
					<Label htmlFor="value-edit">{getLabel()}</Label>
					<Input
						id="value-edit"
						type="password"
						value={value}
						onChange={(e) => setValue(e.target.value)}
						placeholder="Enter secret value"
						autoComplete="off"
					/>
					<p className="text-xs text-muted-foreground">
						This is a sensitive value
					</p>
				</div>
			);
		}

		// Default: Text input
		return (
			<div className="space-y-2">
				<Label htmlFor="value-edit">{getLabel()}</Label>
				<Input
					id="value-edit"
					type="text"
					value={value}
					onChange={(e) => setValue(e.target.value)}
					placeholder="Enter value"
				/>
			</div>
		);
	};

	return (
		<div className="p-4 sm:p-6 space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold">System Settings</h1>
					<p className="text-sm text-muted-foreground mt-1">
						Manage global system configuration
					</p>
				</div>
			</div>

			{isLoading ? (
				<div className="flex justify-center items-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			) : settings.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-16 px-8 text-center border rounded-lg bg-muted/50">
					<SettingsIcon className="h-12 w-12 text-muted-foreground mb-4" />
					<h3 className="text-lg font-semibold">No system settings</h3>
					<p className="text-sm text-muted-foreground mt-2">
						No system settings have been configured yet
					</p>
				</div>
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-[280px]">Key</TableHead>
							<TableHead className="w-[100px]">Type</TableHead>
							<TableHead className="w-[220px]">Value</TableHead>
							<TableHead className="w-[160px]">Category</TableHead>
							<TableHead className="w-[180px]">Last Updated</TableHead>
							<TableHead className="w-12" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{settings.map((setting) => (
							<TableRow
								key={setting.key}
								onClick={() => handleRowClick(setting)}
								className="cursor-pointer hover:bg-muted/50"
							>
								<TableCell className="font-mono text-xs">
									{setting.key}
								</TableCell>
								<TableCell>{getValueTypeBadge(setting)}</TableCell>
								<TableCell className="font-medium">
									{formatValue(setting)}
								</TableCell>
								<TableCell>
									<StatusBadge type="default">{setting.category}</StatusBadge>
								</TableCell>
								<TableCell className="text-muted-foreground text-sm">
									{formatDate(setting.updatedAt)}
								</TableCell>
								<TableCell>
									<ChevronRight className="h-4 w-4 text-muted-foreground" />
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			<Drawer
				open={isDrawerOpen}
				onOpenChange={(open) => {
					setIsDrawerOpen(open);
					if (!open) {
						setTimeout(() => setSelectedSetting(null), 300);
					}
				}}
				direction="right"
			>
				<DrawerContent className="h-screen overflow-hidden flex flex-col w-full max-w-full sm:max-w-sm md:max-w-[600px]">
					<div className="flex flex-col h-full w-full">
						<DrawerHeader className="flex-shrink-0">
							<DrawerTitle className="text-lg font-medium">
								Edit System Setting
							</DrawerTitle>
							{selectedSetting && (
								<DrawerDescription className="text-sm text-muted-foreground font-mono">
									{selectedSetting.key}
								</DrawerDescription>
							)}
						</DrawerHeader>

						<Separator />

						<div className="flex-1 overflow-y-auto">
							{selectedSetting && (
								<div className="px-6 pb-6 space-y-6 mt-6">
									<div className="space-y-4">
										<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
											Details
										</h3>
										<div className="space-y-3">
											<div>
												<div className="text-xs font-medium text-muted-foreground mb-1">
													Key
												</div>
												<div className="font-mono text-sm break-all">
													{selectedSetting.key}
												</div>
											</div>
											<div>
												<div className="text-xs font-medium text-muted-foreground mb-1">
													Category
												</div>
												<div className="text-sm">
													<StatusBadge type="default">
														{selectedSetting.category}
													</StatusBadge>
												</div>
											</div>
											<div>
												<div className="text-xs font-medium text-muted-foreground mb-1">
													Type
												</div>
												<div className="text-sm">
													{getValueTypeBadge(selectedSetting)}
												</div>
											</div>
											{selectedSetting.description && (
												<div>
													<div className="text-xs font-medium text-muted-foreground mb-1">
														Description
													</div>
													<div className="text-sm text-muted-foreground">
														{selectedSetting.description}
													</div>
												</div>
											)}
											<div>
												<div className="text-xs font-medium text-muted-foreground mb-1">
													Last Updated
												</div>
												<div className="text-sm">
													{formatDate(selectedSetting.updatedAt)}
												</div>
											</div>
										</div>
									</div>

									<Separator />

									<div className="space-y-4">
										<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
											Value
										</h3>
										{renderValueInput(selectedSetting, editValue, setEditValue)}
									</div>

									{selectedSetting.validationRules && (
										<>
											<Separator />
											<div className="space-y-4">
												<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
													Validation Rules
												</h3>
												<pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto">
													{JSON.stringify(
														selectedSetting.validationRules,
														null,
														2,
													)}
												</pre>
											</div>
										</>
									)}
								</div>
							)}
						</div>

						<DrawerFooter className="flex-shrink-0">
							<div className="flex gap-3">
								<Button
									onClick={handleSave}
									disabled={isSaving}
									className="flex-1"
									size="lg"
								>
									{isSaving ? (
										<>
											<Loader2 className="h-4 w-4 mr-2 animate-spin" />
											Saving...
										</>
									) : (
										"Save Changes"
									)}
								</Button>
								<DrawerClose asChild>
									<Button
										variant="outline"
										className="flex-1"
										size="lg"
										disabled={isSaving}
									>
										Cancel
									</Button>
								</DrawerClose>
							</div>
						</DrawerFooter>
					</div>
				</DrawerContent>
			</Drawer>
		</div>
	);
};

export default ManageSystemSettingsPage;
